import React, { useCallback, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit3, Trash2, FileText, Check, Info } from 'lucide-react';
import styles from './NewSubProjectManagerV2.module.scss';
import { 
  NewSubProject, 
  NewProjectValidationState 
} from '../../../types/newProject';
import { newValidationService } from '../../../services/newValidationService.ts';

interface NewSubProjectManagerV2Props {
  subProjects: NewSubProject[];
  validation: NewProjectValidationState;
  onAddSubProject: (subProject: NewSubProject) => void;
  onUpdateSubProject: (subProjectId: string, data: Partial<NewSubProject>) => void;
  onRemoveSubProject: (subProjectId: string) => void;
  onOpenSubProjectModal: (subProjectId?: string) => void;
  isEditing?: boolean;
  isLoading?: boolean;
  projectStartDate?: string;
  projectEndDate?: string;
  primaryAction?: React.ReactNode;
  managerFooter?: React.ReactNode;
  lastAddedSubProjectId?: string | null;
}

const NewSubProjectManagerV2: React.FC<NewSubProjectManagerV2Props> = ({
  subProjects,
  validation,
  onAddSubProject,
  onUpdateSubProject,
  onRemoveSubProject,
  onOpenSubProjectModal,
  isEditing = false,
  isLoading = false,
  projectStartDate,
  projectEndDate,
  primaryAction,
  managerFooter,
  lastAddedSubProjectId
}) => {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [rubrosInfoOpen, setRubrosInfoOpen] = useState(false);
  const rubrosInfoWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!lastAddedSubProjectId) return;
    setHighlightedId(lastAddedSubProjectId);
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-subproject-id="${lastAddedSubProjectId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    const t = setTimeout(() => setHighlightedId(null), 2200);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [lastAddedSubProjectId]);

  useEffect(() => {
    if (!rubrosInfoOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rubrosInfoWrapRef.current && !rubrosInfoWrapRef.current.contains(e.target as Node)) {
        setRubrosInfoOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRubrosInfoOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [rubrosInfoOpen]);

  const handleAddSubProject = useCallback(() => {
    onOpenSubProjectModal('new');
  }, [onOpenSubProjectModal]);

  // Eliminar subproyecto
  const handleRemoveSubProject = useCallback((subProjectId: string) => {
    onRemoveSubProject(subProjectId);
  }, [onRemoveSubProject]);

  // Editar subproyecto
  const handleEditSubProject = useCallback((subProjectId: string) => {
    onOpenSubProjectModal(subProjectId);
  }, [onOpenSubProjectModal]);


  // Validar subproyecto
  const isSubProjectValid = useCallback((subProject: NewSubProject): boolean => {
    return newValidationService.validateSubProjectData(subProject);
  }, []);

  // Obtener errores de validación para un subproyecto
  const getSubProjectErrors = useCallback((subProjectId: string) => {
    return validation.subProjects[subProjectId] || {};
  }, [validation.subProjects]);

  // Obtener estado del subproyecto
  const getSubProjectStatus = useCallback((subProject: NewSubProject) => {
    if (subProject.isNew) return 'new';
    if (subProject.isEdited) return 'edited';
    return 'existing';
  }, []);

  return (
    <div className={styles.subProjectManager}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.headerText}>
            <h3 className={styles.title}>Rubros a cotizar</h3>
            <p className={styles.subtitle}>Agregá los trabajos que necesitás cotizar</p>
          </div>
          <div className={styles.infoButtonWrap} ref={rubrosInfoWrapRef}>
            <button
              type="button"
              className={styles.infoButton}
              aria-expanded={rubrosInfoOpen}
              aria-haspopup="dialog"
              aria-label="Más información sobre cómo funcionan los rubros"
              onClick={() => setRubrosInfoOpen((o) => !o)}
            >
              <Info size={16} />
            </button>
            {rubrosInfoOpen ? (
              <div
                className={styles.infoPopover}
                role="dialog"
                aria-label="Cómo funcionan los rubros"
              >
                <button
                  type="button"
                  className={styles.infoPopoverClose}
                  onClick={() => setRubrosInfoOpen(false)}
                  aria-label="Cerrar"
                >
                  ×
                </button>
                <h4 className={styles.infoPopoverTitle}>Cómo funcionan los rubros</h4>
                <p className={styles.infoPopoverIntro}>
                  Organizá tu proyecto en rubros para recibir cotizaciones de forma más clara y ordenada.
                </p>
                <ul className={styles.infoPopoverList}>
                  <li>Podés crear varios rubros con distintas fechas para escalonar la recepción de cotizaciones.</li>
                  <li>Siempre decidís quién puede ver la documentación que sumás en cada rubro.</li>
                  <li>
                    Cuanta más documentación agregues (planos, especificaciones, referencias), mejores van a ser
                    las cotizaciones.
                  </li>
                  <li>Mientras la publicación esté activa, podés agregar, editar o eliminar rubros cuando lo necesites.</li>
                </ul>
                <p className={styles.infoPopoverFooter}>
                  ¿Dudas? Abrí el asistente con IA desde el botón abajo a la derecha o escribinos a{' '}
                  <a href="mailto:contacto@edify.la" className={styles.infoPopoverLink}>
                    contacto@edify.la
                  </a>
                  .
                </p>
              </div>
            ) : null}
          </div>
        </div>
        {subProjects.length > 0 && (
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAddSubProject}
            disabled={isLoading}
          >
            <Plus size={18} />
            Agregar rubro
          </button>
        )}
      </div>

      <div className={styles.subProjectsListScroll}>
        <div className={styles.subProjectsList}>
          {subProjects.length === 0 ? (
            <motion.div
              key="empty-state"
              className={styles.emptyState}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className={styles.emptyStateIcon}>
                <FileText size={48} />
              </div>
              <h4 className={styles.emptyStateTitle}>No hay rubros a cotizar</h4>
              <p className={styles.emptyStateText}>
                Agrega al menos un rubro para continuar
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.5 }}
              className={styles.subProjectsListInner}
            >
            {subProjects.map((subProject, index) => {
              const errors = getSubProjectErrors(subProject.id || index.toString());
              const isValid = isSubProjectValid(subProject);
              const status = getSubProjectStatus(subProject);
              
              return (
                <motion.div
                  key={subProject.id}
                  data-subproject-id={subProject.id}
                  className={`${styles.subProjectItem} ${styles[status]} ${!isValid ? styles.invalid : ''} ${highlightedId === subProject.id ? styles.highlighted : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >

                  <div className={styles.subProjectMain}>
                    <div className={styles.subProjectContent}>
                      <div className={styles.subProjectHeader}>
                        <h4 className={styles.subProjectName}>
                          {subProject.name || 'Rubro sin nombre'}
                        </h4>
                        <div className={styles.subProjectActions}>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => handleEditSubProject(subProject.id!)}
                            disabled={isLoading}
                            title="Editar rubro"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            onClick={() => handleRemoveSubProject(subProject.id!)}
                            disabled={isLoading}
                            title="Eliminar rubro"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    {Object.keys(errors).length > 0 ? (
                      <div className={styles.validationErrors}>
                        {Object.values(errors).map((error, errorIndex) => (
                          <span key={errorIndex} className={styles.errorText}>
                            {error}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>

                </motion.div>
              );
            })
            }
            </motion.div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInfo}>
          <span className={styles.subProjectCount}>
            {subProjects.length} rubro{subProjects.length !== 1 ? 's' : ''}
          </span>
          {subProjects.length > 0 && (
            <span className={styles.validCount}>
              {subProjects.filter(isSubProjectValid).length} válido{subProjects.filter(isSubProjectValid).length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {validation.global.hasSubProjects && (
          <div className={styles.globalError}>
            {validation.global.hasSubProjects}
          </div>
        )}
      </div>
      {primaryAction && (
        <div className={styles.primaryAction}>
          {primaryAction}
        </div>
      )}
      {managerFooter ? (
        <div className={styles.subProjectManagerFooterExtras}>{managerFooter}</div>
      ) : null}
      {lastAddedSubProjectId && (
        <motion.div
          className={styles.rubroAddedToast}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Check size={20} className={styles.rubroAddedToast_icon} />
          <span>Rubro agregado</span>
        </motion.div>
      )}
    </div>
  );
};

export default NewSubProjectManagerV2;
